import { zoneOptions } from '../../lib/time.js'

const Fez = window.Fez;
if (!Fez) throw new Error('fez runtime not loaded: load fez (window.Fez) before component modules');

// --- input-time-zone ---
let __fez_component_0;
{
// Most rows a search shows at once; typing narrows the rest.
const SHOWN = 120

// What a search matches besides the name: the offset as people type it.
function offsetWords(minutes) {
  const sign = minutes < 0 ? '-' : '+'
  const abs = Math.abs(minutes)
  const h = Math.floor(abs / 60)
  const m = String(abs % 60).padStart(2, '0')
  const short = abs % 60 ? `${sign}${h}:${m}` : `${sign}${h}`
  const long = `${sign}${String(h).padStart(2, '0')}:${m}`
  return [short, long, `utc${short}`, `gmt${short}`, `utc${long}`, `gmt${long}`].join(' ')
}
__fez_component_0 = class {
  PROPS = {
    name: String,
    label: String,
    value: String,
    hint: String,
    error: String,
    disabled: Boolean,
    on_change: Function,
  }

  init(props) {
    this.state.options = zoneOptions(props.value).map((option) => ({
      ...option,
      words: `${option.value} ${option.value.replace(/_/g, ' ')} ${offsetWords(option.offset)}`.toLowerCase(),
    }))
    this.state.current = props.value || ''
    this.state.text = this.labelOf(this.state.current)
    this.state.query = ''
    this.state.open = false
    this.state.active = 0
  }

  onRefresh(props, changed) {
    if ('value' in changed && (props.value || '') !== this.state.current) {
      if (props.value && !this.state.options.some((option) => option.value === props.value)) this.init(props)
      this.state.current = props.value || ''
      this.state.text = this.labelOf(this.state.current)
    }
  }

  labelOf(zone) {
    return this.state.options.find((option) => option.value === zone)?.label || zone || ''
  }

  value() {
    return this.state.current
  }

  openList() {
    if (this.props.disabled) return
    this.state.query = ''
    this.state.open = true
    this.state.active = Math.max(0, this.state.options.findIndex((option) => option.value === this.state.current))
    this.state.field?.select()
    this.reveal()
  }

  search(text) {
    this.state.text = text
    this.state.query = text
    this.state.open = true
    this.state.active = 0
  }

  pick(zone) {
    this.state.current = zone
    this.state.text = this.labelOf(zone)
    this.state.open = false
    this.props.on_change?.(zone)
  }

  blurred() {
    // A click on a row lands first (mousedown keeps the focus); otherwise the last pick stays.
    this.state.open = false
    this.state.text = this.labelOf(this.state.current)
  }

  key(event) {
    const shown = this.state.shown || []
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      if (!this.state.open) return this.openList()
      const step = event.key === 'ArrowDown' ? 1 : -1
      this.state.active = Math.min(Math.max(this.state.active + step, 0), Math.max(shown.length - 1, 0))
      this.reveal()
    } else if (event.key === 'Enter') {
      if (!this.state.open) return
      event.preventDefault()
      const option = shown[this.state.active]
      if (option) this.pick(option.value)
    } else if (event.key === 'Escape' && this.state.open) {
      event.preventDefault()
      event.stopPropagation()
      this.blurred()
    }
  }

  async reveal() {
    await this.tick()
    this.find('.tz-option.active')?.scrollIntoView({ block: 'nearest' })
  }

  beforeRender() {
    const words = this.state.query.trim().toLowerCase().split(/\s+/).filter(Boolean)
    const found = words.length ? this.state.options.filter((option) => words.every((word) => option.words.includes(word))) : this.state.options
    this.state.shown = found.slice(0, SHOWN).map((option, index) => ({ ...option, index, active: index === this.state.active, chosen: option.value === this.state.current }))
    this.state.more = found.length - this.state.shown.length
  }

  CSS = `:fez {
display:block;position:relative;.tz-label{display:grid;gap:6px}.tz-title{font-size:var(--text-sm);font-weight:var(--weight-semibold)}.tz-control{position:relative;display:flex;align-items:center;.ui-icon-fez{position:absolute;right:var(--space-3);pointer-events:none;color:var(--color-text-subtle)}}.tz-search{width:100%;height:38px;padding:0 var(--space-8) 0 var(--space-3);border:1px solid var(--color-border-strong);border-radius:var(--radius-md);background:var(--color-bg-raised);color:var(--color-text);font-size:var(--text-md);&:focus{outline:0;border-color:var(--color-accent);box-shadow:0 0 0 3px var(--color-accent-soft)}&:disabled{color:var(--color-text-muted);background:var(--color-bg-subtle)}}.tz-list{position:absolute;z-index:30;left:0;right:0;max-height:280px;margin:var(--space-1) 0 0;padding:var(--space-1);overflow-y:auto;list-style:none;border:1px solid var(--color-border);border-radius:var(--radius-md);background:var(--color-bg-raised);box-shadow:var(--shadow-lg)}.tz-option{padding:var(--space-2) var(--space-3);border-radius:var(--radius-sm);font-size:var(--text-sm);cursor:pointer;&.active{background:var(--color-bg-subtle)}&.chosen{font-weight:var(--weight-semibold)}}.tz-none{padding:var(--space-2) var(--space-3);color:var(--color-text-muted);font-size:var(--text-sm)}.tz-hint,.tz-error{display:block;margin-top:6px;font-size:var(--text-xs);color:var(--color-text-muted)}.tz-error{color:var(--color-danger)}
}`

  HTML = `<label class="tz-label">
{#if props.label}
<span class="tz-title">{props.label}</span>
{/if}
<span class="tz-control">
<input class="tz-search" type="text" role="combobox" autocomplete="off" spellcheck="false" aria-expanded={state.open ? 'true' : 'false'} placeholder="Search a city or an offset" value={state.text} :disabled="props.disabled" fez:this="field" oninput="fez.search(this.value)" onfocus="fez.openList()" onclick="fez.state.open || fez.openList()" onkeydown="fez.key(event)" onblur="fez.blurred()">
<ui-icon name="caret-down" size="14"></ui-icon>
</span>
</label>
<input type="hidden" name={props.name} value={state.current}>
{#if state.open}
<ul class="tz-list" role="listbox">
{#each state.shown as option}
<li class="tz-option" class:active={option.active} class:chosen={option.chosen} role="option" aria-selected={option.chosen ? 'true' : 'false'} onmousedown="event.preventDefault(); fez.pick('{option.value}')">{option.label}</li>
{/each}
{#if !state.shown.length}
<li class="tz-none">No time zone matches.</li>
{:else if state.more}
<li class="tz-none">{state.more} more: type to narrow.</li>
{/if}
</ul>
{/if}
{#if props.error}
<span class="tz-error" role="alert">{props.error}</span>
{:else if props.hint}
<span class="tz-hint">{props.hint}</span>
{/if}`
};
}

Fez('input-time-zone', __fez_component_0);

export default __fez_component_0;
